import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsMerge, Warning } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

const DupRow = ({ p, keep, onKeep }) => (
  <button
    type="button"
    data-testid={`dup-project-${p.id}`}
    onClick={() => onKeep(p.id)}
    className={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors duration-150 ${
      keep ? "border-emerald-500/50 bg-emerald-500/[0.08]" : "border-zinc-800 hover:border-zinc-600"
    }`}
  >
    <span
      className={`h-3.5 w-3.5 shrink-0 rounded-full border-2 ${keep ? "border-emerald-400 bg-emerald-400" : "border-zinc-600"}`}
    />
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm font-semibold text-zinc-100">
        {p.code} <span className="font-normal text-zinc-400">· {p.name}</span>
      </span>
      <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
        {p.stage}
        {p.has_job ? ` · ${p.job_code} (${p.job_stage})` : " · no job"}
        {p.quotes ? ` · ${p.quotes} quote${p.quotes > 1 ? "s" : ""}` : ""}
        {p.files ? ` · ${p.files} file${p.files > 1 ? "s" : ""}` : ""}
      </span>
    </span>
    <span className={`text-[10px] font-bold uppercase tracking-widest ${keep ? "text-emerald-300" : "text-zinc-600"}`}>
      {keep ? "Keep" : "Merge"}
    </span>
  </button>
);

const DupGroup = ({ g, onMerged }) => {
  const [keep, setKeep] = useState(g.keep_id);
  const [busy, setBusy] = useState(false);
  const merge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/projects/merge-group", {
        into_id: keep,
        from_ids: g.projects.map((p) => p.id).filter((id) => id !== keep),
      });
      if (data.merged.length) toast.success(`Merged ${data.merged.join(", ")} into ${data.code}`);
      data.skipped.forEach((s) => toast.warning(`${s.code}: ${s.reason}`));
      onMerged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not merge");
    }
    setBusy(false);
  };
  return (
    <section
      data-testid={`dup-group-${g.key.replace(/[^a-z0-9]+/gi, "-")}`}
      className="rounded-lg border border-zinc-800 bg-zinc-950 p-4"
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-bold text-zinc-100">{g.customer_name || "Customer"}</div>
          <div className="truncate text-xs text-zinc-500">{g.job_address}</div>
        </div>
        <Button
          data-testid={`dup-group-merge-${g.keep_id}`}
          size="sm"
          disabled={busy}
          onClick={merge}
          className="h-9 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
        >
          <ArrowsMerge size={14} weight="bold" className="mr-1.5" />
          Merge {g.projects.length - 1} into {g.projects.find((p) => p.id === keep)?.code}
        </Button>
      </div>
      {g.blocked && (
        <div className="mb-2 flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/[0.08] px-3 py-2 text-xs text-amber-200">
          <Warning size={14} weight="fill" /> More than one of these has a job — those will be skipped.
        </div>
      )}
      <div className="space-y-1.5">
        {g.projects.map((p) => (
          <DupRow key={p.id} p={p} keep={p.id === keep} onKeep={setKeep} />
        ))}
      </div>
    </section>
  );
};

export const DuplicateProjectsDialog = ({ open, onOpenChange, onChanged }) => {
  const [groups, setGroups] = useState(null);
  const scan = async () => {
    setGroups(null);
    try {
      const { data } = await api.get("/projects/duplicates");
      setGroups(data.groups);
    } catch {
      setGroups([]);
      toast.error("Could not scan for duplicates");
    }
  };
  useEffect(() => {
    if (open) scan();
  }, [open]);
  const merged = () => {
    onChanged?.();
    scan();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="duplicates-dialog" className="max-h-[85vh] overflow-y-auto border-zinc-800 bg-zinc-900 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base font-bold tracking-tight">Duplicate projects</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Same customer and job site. Pick which one to keep, then merge — quotes, invoices,
            files and the job move onto the kept project.
          </DialogDescription>
        </DialogHeader>
        {groups === null ? (
          <div data-testid="duplicates-loading" className="py-8 text-center text-xs uppercase tracking-widest text-zinc-500">
            Scanning…
          </div>
        ) : groups.length === 0 ? (
          <div data-testid="duplicates-empty" className="rounded-lg border border-dashed border-zinc-800 py-8 text-center text-sm text-zinc-500">
            No duplicates — every customer + site has one project.
          </div>
        ) : (
          <div className="space-y-3">
            <div data-testid="duplicates-count" className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              {groups.length} group{groups.length > 1 ? "s" : ""} found
            </div>
            {groups.map((g) => (
              <DupGroup key={g.key} g={g} onMerged={merged} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
